import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import QuoteCommentsPanel from '../components/QuoteCommentsPanel';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { RefreshCw, CheckCircle2, FileText, Image, Download, Eye, FileSpreadsheet, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StatusUpdatesPage() {
  const { user } = useAuth();
  const [statusData, setStatusData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({ from: '', to: '' });
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [completionDate, setCompletionDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [jobsheetPanelOpen, setJobsheetPanelOpen] = useState(false);
  const [selectedJobsheet, setSelectedJobsheet] = useState(null);
  const [loadingJobsheet, setLoadingJobsheet] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStatusUpdates();
  }, []);

  const fetchStatusUpdates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/status-updates`);
      setStatusData(response.data);
    } catch (error) {
      toast.error('Failed to load status updates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCompletionDialog = (item) => {
    setSelectedQuote(item);
    setCompletionDate(new Date().toISOString().split('T')[0]);
    setCompletionDialogOpen(true);
  };

  const handleMarkAsCompleted = async () => {
    if (!completionDate) {
      toast.error('Completion date is required');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes/${selectedQuote.quote_id}/work-completed`, {
        completion_date: completionDate
      });
      toast.success('Work marked as completed successfully');
      setCompletionDialogOpen(false);
      setSelectedQuote(null);
      fetchStatusUpdates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as completed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenJobsheetPanel = async (item) => {
    setSelectedJobsheet({ quote_no: item.quote_no, quote_id: item.quote_id });
    setJobsheetPanelOpen(true);
    setLoadingJobsheet(true);
    
    try {
      const response = await axios.get(`${API}/quotes/${item.quote_id}/jobsheet`);
      setSelectedJobsheet({
        quote_no: item.quote_no,
        quote_id: item.quote_id,
        ...response.data
      });
    } catch (error) {
      console.error('Failed to fetch jobsheet details', error);
      toast.error('Failed to load jobsheet details');
    } finally {
      setLoadingJobsheet(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!selectedJobsheet) return;
    
    try {
      toast.info('Merging documents into PDF...');
      
      const response = await axios.get(
        `${API}/quotes/${selectedJobsheet.quote_id}/merge-documents`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedJobsheet.quote_no}-merged.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Merged PDF downloaded successfully');
    } catch (error) {
      console.error('Merge failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to merge documents');
    }
  };

  const handleExportToExcel = async () => {
    // Open the date range dialog
    setExportDateRange({ from: '', to: '' });
    setExportDialogOpen(true);
  };

  const handleConfirmExport = async () => {
    if (!exportDateRange.from || !exportDateRange.to) {
      toast.error('Please select both From and To dates');
      return;
    }

    if (new Date(exportDateRange.from) > new Date(exportDateRange.to)) {
      toast.error('From date cannot be later than To date');
      return;
    }

    setExporting(true);
    setExportDialogOpen(false);
    
    try {
      const response = await axios.get(`${API}/status-updates/export`, {
        params: {
          from_date: exportDateRange.from,
          to_date: exportDateRange.to
        },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fromDate = exportDateRange.from.replace(/-/g, '');
      const toDate = exportDateRange.to.replace(/-/g, '');
      link.setAttribute('download', `Status_Updates_${fromDate}_to_${toDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export to Excel');
    } finally {
      setExporting(false);
    }
  };

  const isSiteSupervisor = user?.roles?.includes('Site Supervisor');

  // Filter status data based on search query
  const filteredStatusData = statusData.filter(item => {
    if (!searchQuery.trim()) return true;
    return item.quote_no?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getMaterialStatusBadge = (status) => {
    if (status === 'Delivered') {
      return <Badge className="bg-green-100 text-green-800">Delivered</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-600">NA</Badge>;
  };

  const getJobStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: 'Ready for Work', className: 'bg-yellow-100 text-yellow-800' },
      'work_in_progress': { label: 'Work In Progress', className: 'bg-blue-100 text-blue-800' },
      'work_completed': { label: 'Jobsheet Pending', className: 'bg-orange-100 text-orange-800' },  // Same as jobsheet_pending
      'work_cancelled': { label: 'Work Cancelled', className: 'bg-red-100 text-red-800' },
      'jobsheet_pending': { label: 'Jobsheet Pending', className: 'bg-orange-100 text-orange-800' },
      'jobsheet_submitted': { label: 'Jobsheet Submitted', className: 'bg-purple-100 text-purple-800' },
      'billing_approved': { label: 'Completed', className: 'bg-green-100 text-green-800' },
      'billing_rejected': { label: 'Billing Rejected', className: 'bg-red-100 text-red-800' },
    };
    
    const config = statusMap[status] || { label: 'NA', className: 'bg-gray-100 text-gray-600' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getJobSheetStatusBadge = (status, item = null) => {
    if (status === 'Pending') {
      return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
    } else if (status === 'Pending For Review') {
      return <Badge className="bg-blue-100 text-blue-800">Pending For Review</Badge>;
    } else if (status === 'Approved') {
      return (
        <div 
          className="inline-flex items-center gap-1 cursor-pointer hover:opacity-80"
          onClick={() => item && handleOpenJobsheetPanel(item)}
          data-testid={`jobsheet-approved-${item?.quote_id}`}
        >
          <Badge className="bg-green-100 text-green-800">Approved</Badge>
          <Eye className="h-3 w-3 text-gray-400" />
        </div>
      );
    } else if (status === 'Rejected') {
      return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-600">NA</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading status updates...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="status-updates-page">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Status Updates
            </h1>
            <p className="text-muted-foreground mt-2">
              Track quote status across all workflows
            </p>
            <div className="relative mt-4 max-w-xs">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by Quote Number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-quote-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExportToExcel} variant="outline" disabled={exporting || loading} data-testid="export-excel-btn">
              <FileSpreadsheet className={`h-4 w-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
            <Button onClick={fetchStatusUpdates} variant="outline" disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {filteredStatusData.length === 0 ? (
          <Card className="p-12 text-center glass-card">
            <p className="text-muted-foreground">{searchQuery ? 'No quotes found matching your search' : 'No quotes found'}</p>
          </Card>
        ) : (
          <Card className="overflow-hidden glass-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1B273D] text-white">
                    <th className="px-4 py-3 text-left text-sm font-semibold">Quote No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Quotation Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Site Supervisor Name</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Job Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Job Sheet Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredStatusData.map((item) => (
                    <tr key={item.quote_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center">
                          <span className="font-mono font-medium text-[#DC143C]">{item.quote_no}</span>
                          {/* Hide comments panel for Site Supervisors */}
                          {!isSiteSupervisor && <QuoteCommentsPanel quoteId={item.quote_id} quoteNo={item.quote_no} />}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatDate(item.quotation_date)}
                      </td>
                      <td className="px-4 py-3 text-sm max-w-xs">
                        <p className="break-all whitespace-pre-wrap line-clamp-2" title={item.supervisor_name}>
                          {item.supervisor_name || '-'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-center gap-1">
                          {getJobStatusBadge(item.job_status)}
                          {item.job_status_date && (
                            <span className="text-xs text-gray-500">{formatDate(item.job_status_date)}</span>
                          )}
                          {item.job_status === 'work_cancelled' && item.work_cancelled_remarks && (
                            <p className="text-xs text-red-600 mt-1 max-w-32 break-all whitespace-pre-wrap line-clamp-2" title={item.work_cancelled_remarks}>
                              {item.work_cancelled_remarks}
                            </p>
                          )}
                          {/* Mark as Completed button for Site Supervisor */}
                          {isSiteSupervisor && item.job_status === 'work_in_progress' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-2 text-xs h-7 bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                              onClick={() => handleOpenCompletionDialog(item)}
                              data-testid={`mark-completed-btn-${item.quote_id}`}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Mark Completed
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getJobSheetStatusBadge(item.jobsheet_status, item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Export Date Range Dialog */}
        <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Export to Excel</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Select the date range for Quotation Date to export status updates.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="export_from_date">From Date *</Label>
                  <Input
                    id="export_from_date"
                    type="date"
                    value={exportDateRange.from}
                    onChange={(e) => setExportDateRange({ ...exportDateRange, from: e.target.value })}
                    required
                    data-testid="export-from-date"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="export_to_date">To Date *</Label>
                  <Input
                    id="export_to_date"
                    type="date"
                    value={exportDateRange.to}
                    onChange={(e) => setExportDateRange({ ...exportDateRange, to: e.target.value })}
                    required
                    data-testid="export-to-date"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleConfirmExport}
                  disabled={!exportDateRange.from || !exportDateRange.to}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="confirm-export-button"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Ok
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setExportDialogOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Mark as Completed Dialog */}
        <Dialog open={completionDialogOpen} onOpenChange={setCompletionDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Mark Work as Completed</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Quote: <span className="font-medium">{selectedQuote?.quote_no}</span></p>
                <p className="text-sm text-gray-600">Store: <span className="font-medium">{selectedQuote?.store_details}</span></p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="completion_date">Work Completion Date *</Label>
                <Input
                  id="completion_date"
                  type="date"
                  value={completionDate}
                  onChange={(e) => setCompletionDate(e.target.value)}
                  required
                  data-testid="completion-date-input"
                />
              </div>

              <p className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
                After marking as completed, you can upload the job sheet from the Quote Workflow tab.
              </p>

              <div className="flex gap-2">
                <Button
                  onClick={handleMarkAsCompleted}
                  disabled={submitting || !completionDate}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="confirm-completion-button"
                >
                  {submitting ? 'Processing...' : 'Confirm Completed'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCompletionDialogOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Jobsheet Details Sheet */}
        <Sheet open={jobsheetPanelOpen} onOpenChange={setJobsheetPanelOpen}>
          <SheetContent side="right" className="w-[450px] sm:w-[500px] overflow-y-auto">
            <SheetHeader className="pb-4 border-b">
              <SheetTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-600" />
                Job Sheet Documents
              </SheetTitle>
              <p className="text-sm text-muted-foreground">Quote: {selectedJobsheet?.quote_no}</p>
            </SheetHeader>
            
            <div className="space-y-6 py-6">
              {loadingJobsheet ? (
                <div className="text-center text-sm text-gray-500 py-8">Loading documents...</div>
              ) : (
                <>
                  {/* Job Sheet Document */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <FileText className="h-4 w-4 text-blue-600" />
                      Job Sheet
                    </div>
                    {selectedJobsheet?.jobsheet_path ? (
                      <div className="rounded-lg border overflow-hidden bg-gray-50">
                        <div className="p-3 flex items-center justify-between">
                          <span className="text-sm text-gray-600 truncate flex-1">
                            {selectedJobsheet.jobsheet_path.split('/').pop()}
                          </span>
                          <a
                            href={`${BACKEND_URL}/api/uploads/${selectedJobsheet.jobsheet_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" /> View
                          </a>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No job sheet uploaded</p>
                    )}
                  </div>

                  {/* Before Photo */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Image className="h-4 w-4 text-orange-600" />
                      Before Photo
                    </div>
                    {selectedJobsheet?.before_photo_path ? (
                      <div className="rounded-lg border overflow-hidden bg-gray-100">
                        <img 
                          src={`${BACKEND_URL}/api/uploads/${selectedJobsheet.before_photo_path}`}
                          alt="Before"
                          className="w-full h-auto max-h-[200px] object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="p-2 bg-white border-t flex justify-end">
                          <a
                            href={`${BACKEND_URL}/api/uploads/${selectedJobsheet.before_photo_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" /> View Full
                          </a>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No before photo uploaded</p>
                    )}
                  </div>

                  {/* After Photo */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Image className="h-4 w-4 text-green-600" />
                      After Photo
                    </div>
                    {selectedJobsheet?.after_photo_path ? (
                      <div className="rounded-lg border overflow-hidden bg-gray-100">
                        <img 
                          src={`${BACKEND_URL}/api/uploads/${selectedJobsheet.after_photo_path}`}
                          alt="After"
                          className="w-full h-auto max-h-[200px] object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="p-2 bg-white border-t flex justify-end">
                          <a
                            href={`${BACKEND_URL}/api/uploads/${selectedJobsheet.after_photo_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" /> View Full
                          </a>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No after photo uploaded</p>
                    )}
                  </div>

                  {/* Download All Button */}
                  {(selectedJobsheet?.jobsheet_path || selectedJobsheet?.before_photo_path || selectedJobsheet?.after_photo_path) && (
                    <div className="pt-4 border-t">
                      <Button
                        onClick={handleDownloadAll}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        data-testid="download-all-jobsheet-btn"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download All Documents
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </Sidebar>
  );
}
